import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Icon, ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { AssessmentsTab } from 'screens/AssessmentsTab/AssessmentTab';
import { SettingsScreen } from 'screens/SettingsScreen/SettingsScreen';
import {
  selectHasAuthToken,
  selectUserFeatureCanViewAssessmentsTab,
  selectUserFeatureCanViewReportingTab,
} from 'store/auth/selectors';

import { ReportingTab } from 'screens/ReportingTab/ReportingTab';
import { TabOfflineScreen } from './TabOfflineScreen';

const Tab = createBottomTabNavigator();
type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ROOT],
  Screens.ROOT_TAB
>;

export const TabScreen: React.FC<Props> = ({ navigation }) => {
  const hasAuthorizationToken = useSelector(selectHasAuthToken);

  React.useEffect(() => {
    if (!hasAuthorizationToken) {
      navigation.replace(Screens.ROOT_AUTH);
    }
  }, [navigation, hasAuthorizationToken]);

  const canViewAssessmentsTab = useSelector(
    selectUserFeatureCanViewAssessmentsTab,
  );

  const canViewReportingTab = useSelector(selectUserFeatureCanViewReportingTab);

  return (
    <Tab.Navigator
      initialRouteName={Screens.TAB_OFFLINE}
      screenOptions={{
        headerShown: false,
        freezeOnBlur: true,
        tabBarItemStyle: {
          paddingTop: 0,
        },
        tabBarIconStyle: {
          height: 0,
          flex: undefined,
          display: 'none',
        },
      }}
    >
      <Tab.Screen
        name={Screens.TAB_OFFLINE}
        component={TabOfflineScreen}
        options={{
          tabBarIcon: () => null,
          tabBarLabel: ({ color, position }) => (
            <View
              style={
                position === 'below-icon'
                  ? styles.tabStackVertical
                  : styles.tabStackHorizontal
              }
            >
              <Icon source={ICONS.HouseDuotone} size={30} color={color} />
              <Txt
                size="xxxsmall"
                weight="bold"
                style={{
                  paddingHorizontal: theme.paddingHorizontal,
                  color,
                }}
                numberOfLines={1}
              >
                Assets
              </Txt>
            </View>
          ),
        }}
      />
      {canViewAssessmentsTab && (
        <Tab.Screen
          name={Screens.TAB_ASSESSMENTS}
          component={AssessmentsTab}
          options={{
            tabBarIcon: () => null,
            tabBarLabel: ({ color, position }) => (
              <View
                style={
                  position === 'below-icon'
                    ? styles.tabStackVertical
                    : styles.tabStackHorizontal
                }
              >
                <Icon source={ICONS.FileDuotone} size={30} color={color} />
                <Txt
                  size="xxxsmall"
                  weight="bold"
                  style={{
                    paddingHorizontal: theme.paddingHorizontal,
                    color,
                  }}
                  numberOfLines={1}
                >
                  QC & Approved
                </Txt>
              </View>
            ),
          }}
        />
      )}
      {canViewReportingTab && (
        <Tab.Screen
          name={Screens.TabReporting}
          component={ReportingTab}
          options={{
            tabBarIcon: () => null,
            tabBarLabel: ({ color, position }) => (
              <View
                style={
                  position === 'below-icon'
                    ? styles.tabStackVertical
                    : styles.tabStackHorizontal
                }
              >
                <Icon source={ICONS.ChartBarDuotone} size={30} color={color} />
                <Txt
                  size="xxxsmall"
                  weight="bold"
                  style={{
                    paddingHorizontal: theme.paddingHorizontal,
                    color,
                  }}
                  numberOfLines={1}
                >
                  Reporting
                </Txt>
              </View>
            ),
          }}
        />
      )}
      <Tab.Screen
        name={Screens.TAB_SETTINGS}
        component={SettingsScreen}
        options={{
          tabBarIcon: () => null,
          tabBarLabel: ({ color, position }) => (
            <View
              style={
                position === 'below-icon'
                  ? styles.tabStackVertical
                  : styles.tabStackHorizontal
              }
            >
              <Icon source={ICONS.UserGearDuotone} size={30} color={color} />
              <Txt
                size="xxxsmall"
                weight="bold"
                style={{
                  paddingHorizontal: theme.paddingHorizontal,
                  color,
                }}
                numberOfLines={1}
              >
                Settings
              </Txt>
            </View>
          ),
        }}
      />
    </Tab.Navigator>
  );
};

const styles = StyleSheet.create({
  tabStackHorizontal: {
    height: 53,
    flexDirection: 'row',
    alignItems: 'center',
  },
  tabStackVertical: {
    height: 53,
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
  },
});
